import {
  MedicareApiPlanDetails,
  PreferredProviderNetworkStatuses,
  ProviderNetworkStatus,
} from 'types/api';
import { DBProviders } from 'db/schema/providers';

export const getProviderNetworkStatuses = (
  savedProviders: DBProviders[],
  providerLocations?: PreferredProviderNetworkStatuses
): ProviderNetworkStatus[] => {
  if (savedProviders.length === 0) return [];
  return savedProviders.map((provider) => {
    const status = providerLocations?.in_network?.some(
      (loc) => loc.external_id === provider.provider_id
    )
      ? 'in-network'
      : 'out-of-network';
    return { name: provider.name, status };
  });
};

export const isAdditionalBenefitCovered = (...values: (null | string)[]) =>
  values.some((value) => value);

export const offersDentalBenefits = (planDetails: MedicareApiPlanDetails) => {
  const {
    compPeriodontalIn,
    dentalXrayIn,
    emergencyDental,
    fluorideTreatIn,
    periodontalIn,
    servCleaningIn,
  } = planDetails;

  return isAdditionalBenefitCovered(
    servCleaningIn,
    compPeriodontalIn,
    dentalXrayIn,
    emergencyDental,
    fluorideTreatIn,
    periodontalIn
  );
};

export const offersHearingBenefits = (planDetails: MedicareApiPlanDetails) => {
  const { fittingEvalHearingAid, hearingAids, routineHearingExam } =
    planDetails;

  return isAdditionalBenefitCovered(
    routineHearingExam,
    hearingAids,
    fittingEvalHearingAid
  );
};

export const offersVisionBenefits = (planDetails: MedicareApiPlanDetails) => {
  const { contacts, eyeExamsIn, eyeglassesFramesLenses } = planDetails;

  return isAdditionalBenefitCovered(
    eyeExamsIn,
    contacts,
    eyeglassesFramesLenses
  );
};

export const offersWellnessBenefits = (planDetails: MedicareApiPlanDetails) => {
  const { acupuncture, preventiveServices, wellnessEd } = planDetails;

  return isAdditionalBenefitCovered(
    preventiveServices,
    acupuncture,
    wellnessEd
  );
};

export const getAdditionalBenefits = (
  planLabel: string,
  planData: MedicareApiPlanDetails
) => {
  if (
    (planLabel === 'Vision benefits' && offersVisionBenefits(planData)) ||
    (planLabel === 'Dental benefits' && offersDentalBenefits(planData)) ||
    (planLabel === 'Hearing benefits' && offersHearingBenefits(planData)) ||
    (planLabel === 'Wellness benefits' && offersWellnessBenefits(planData)) ||
    (planLabel === 'Over-the-counter benefits' && planData.otcItems)
  ) {
    return '<span className="checkmark-circle-green"/>';
  } else {
    return '<span className="x-circle-red"/>';
  }
};

export const findPlanType = (type: string): string => {
  const TYPES = {
    mapd: 'Medicare Advantage Plan',
    ma: 'Medicare Advantage Plan',
    gap: 'Medicare Supplement Plan',
    pdp: 'Prescription Drug Plan',
  };

  return TYPES[type as keyof typeof TYPES] || type;
};

// Returns an object that omits the specified keys
export const omitKeys = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> => {
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
};
